import { AbstractEntrustWebPushProvisionning } from '../abstract-web-push-provisioning';
import { Message, MessageOutput, WPPError } from '../common-objects';
import type {
    EntrustWPPIssuerConfig,
    EntrustWPPConfig,
    OnResult,
    OnFailure,
    OnCancel,
    GetPushOpaqueData,
} from '../common-objects';

export class EntrustWebPushProvisionningGoogle extends AbstractEntrustWebPushProvisionning {
    imageButtonUrl = 'https://pay.google.com/gp/v/a/pushprovisioning/saveButtonImage';

    constructor(
        config: EntrustWPPConfig,
        issuerConfig: EntrustWPPIssuerConfig,
        getPushOpaqueDataPromise: GetPushOpaqueData,
        onResult: OnResult,
        onFailure: OnFailure,
        onCancel?: OnCancel
    ) {
        super(config, issuerConfig, getPushOpaqueDataPromise, onResult, onFailure, onCancel);
    }

    postInit(): void {}

    handleButton(): void {
        super.handleButton();
        document.getElementById(this.buttonId)?.addEventListener('click', this.handleGoogle.bind(this));
    }

    handleGoogle(): void {
        (window as any).googlepay.openAppWindow({
            integratorId: this.issuerConfig.integratorId, // Google-assigned Merchant ID.
            tokenSetting: 1, // Provision a device token?
            //   1 => Yes,
            //   0 => No.
            cardSetting: this.issuerConfig.googleOpcEnabled ? 1 : 0, // Save the FPAN to user's Google Account? For v1 we will not support provisioning to Google Account, so this will always be 0.
            cardExtraOptions: {
                virtualCardsSetting: 0, // Enroll for virtual cards?
            },
            isTestEnvironment: this.issuerConfig.environment !== 'production', // Use sandbox instead of prod?
            clientSessionId: crypto.randomUUID(), // A unique ID for this provisioning session, generated by the client. Must be a valid UUID.
            hl: this.getLocale(), // Locale of the user
            onReady: () => {
                //
                // Handle app readiness here.
            },
            onSessionCreated: async (onSessionCreatedPayload) => {
                // See https://developers.google.com/pay/issuers/apis/push-provisioning/web/frontend-javascript-api#library
                // for the data model and structure of onSessionCreatedPayload.

                // Your intended tokenSetting and cardSetting values may be overridden
                // in certain situations. For example, if the user doesn't have an
                // Android device that supports tokenized card payments, tokenSetting
                // will be forced to 0.
                //
                // Always check the tokenSetting and cardSetting values in this payload
                // to find out if this has happened.
                var finalTokenSetting = onSessionCreatedPayload['tokenSetting'];
                var finalCardSetting = onSessionCreatedPayload['cardSetting'];
                if (finalTokenSetting === 0) {
                    // The user is not eligible for token provisioning, or has not consented to token provisioning. Since token provisioning is required for any provisioning to occur in this version, we will not proceed with the provisioning in this case.
                    this.onFailure(
                        new WPPError(
                            Message.TECHNICAL_ERROR,
                            'User is not eligible for token provisioning or did not consent to token provisioning'
                        )
                    );
                    return;
                }

                if (!this.config.google?.cardDisplayName) {
                    this.onFailure(new WPPError(Message.TECHNICAL_ERROR, 'Google Display name is mandatory'));
                    return;
                }

                //
                // ***************************** IMPORTANT *****************************
                // Fetch dynamically generated classic OPC and Google OPC values from
                // your backend server here.
                //
                // * The classic OPC generation should take the public device ID
                //   and/or public wallet ID as input(s). (Depending on the TSP, one or
                //   both may be required.)
                // * The Google OPC generation should take the server session ID as an
                //   input and embed it in the pre-encrypted JSON object.
                //
                // The following sample code fetches the pair of OPCs in two separate
                // RPCs to the issuer's backend server. Depending on your server
                // architecture, you may choose to fetch both in a single RPC instead.
                //
                // Each OPC should be of type Uint8Array, and its contents should be
                // non-URL-safe base64. I.e., its elements should all be numeric ASCII
                // codes of characters in the base 64 alphabet, which are listed in
                // Table 1 of https://datatracker.ietf.org/doc/html/rfc4648#section-4.
                // *********************************************************************

                // generate walletOpaqueData and complete it with the payload received from Google
                try {
                    const walletOpaqueData = await this.generateWalletOpaqueData(onSessionCreatedPayload);
                    const pushOpaqueDataResponse = (await this.getPushOpaqueDataPromise(walletOpaqueData)) as any;
                    const decryptedData = await this.decryptPushOpaqueData(
                        pushOpaqueDataResponse.pushOpaqueData as string
                    );
                    const pushOpaqueDataDecrypted = JSON.parse(new TextDecoder().decode(decryptedData.plaintext));
                    if (!pushOpaqueDataDecrypted?.googlePay?.opc) {
                        this.onFailure(new WPPError(Message.TECHNICAL_ERROR));
                        return;
                    }
                    const paymentCredentials = this.generatePaymentCredentials(pushOpaqueDataDecrypted);
                    if (this.issuerConfig.googleOpcEnabled && finalCardSetting === 1) {
                        if (!pushOpaqueDataDecrypted?.googlePay?.googleOpc) {
                            this.onFailure(new WPPError(Message.TECHNICAL_ERROR));
                            return;
                        }
                        paymentCredentials['cardCredentials'] = {
                            googleOpaquePaymentCard: this.toBase64Bytes(pushOpaqueDataDecrypted.googlePay.googleOpc),
                        };
                    }

                    // Make the API call using the provided JavaScript library.
                    (window as any).googlepay.pushPaymentCredentials(paymentCredentials);
                } catch (error) {
                    this.onFailure(
                        new WPPError(Message.TECHNICAL_ERROR, 'Unable to push payment credentials to Google')
                    );
                }
            },
            onSuccess: (payload) => {
                var tokenResult = payload['tokenResult'];
                // La doc type tokenResult comme une chaîne ('1' / '0').
                if (Number(tokenResult) !== 1) {
                    this.onFailure(new WPPError(MessageOutput.TOKENIZATION_FAILED));
                } else {
                    this.onResult(MessageOutput.OK);
                }
            },
            onFailure: (payload) => {
                var firstErrorCode = payload['errors'][0]['errorCode'];
                //
                // Handle app failure here.
                switch (firstErrorCode) {
                    case 'E201':
                        this.handleCancel(MessageOutput.LOGIN_FAILED);
                        break;
                    case 'E301':
                    case 'E302':
                    case 'E303':
                    case 'E304':
                        this.handleCancel(MessageOutput.TIMEOUT);
                        break;
                    case 'E202':
                        this.handleCancel(MessageOutput.NO_ELIGIBLE_DEVICE);
                        break;
                    case 'E305':
                    case 'E306':
                    case 'E307':
                    case 'E308':
                        this.onFailure(new WPPError(MessageOutput.TOKENIZATION_FAILED));
                        break;
                    default:
                        this.onFailure(new WPPError(MessageOutput.TECHNICAL_ERROR, firstErrorCode));
                        break;
                }
            },
            onCancel: () => {
                // Handle app cancellation here.
                this.handleCancel(MessageOutput.CANCELED);
            },
        });
    }

    // Google expects the ASCII codes of standard (non URL-safe) padded base64.
    toBase64Bytes(urlSafeBase64: string): Uint8Array {
        const unpadded = urlSafeBase64.replace(/-/g, '+').replace(/_/g, '/');
        const padded = unpadded.padEnd(Math.ceil(unpadded.length / 4) * 4, '=');
        return new TextEncoder().encode(padded);
    }

    generatePaymentCredentials(pushOpaqueDataDecrypted): any {
        const paymentCredentials: any = {};
        if (this.config.google?.cardDisplayName) {
            paymentCredentials['displayName'] = this.config.google.cardDisplayName;
        }
        if (this.config.google?.lastDigits) {
            paymentCredentials['lastDigits'] = this.config.google.lastDigits;
        }
        if (this.config.google?.userAddress) {
            paymentCredentials['userAddress'] = this.config.google.userAddress;
        }
        paymentCredentials['tokenCredentials'] = {
            cardNetwork: pushOpaqueDataDecrypted.googlePay.cardNetwork,
            tokenServiceProvider: pushOpaqueDataDecrypted.googlePay.tokenProvider,
            opaquePaymentCard: this.toBase64Bytes(pushOpaqueDataDecrypted.googlePay.opc),
        };
        return paymentCredentials;
    }

    getScriptName(): string {
        return 'integration.min.js';
    }

    generateButton(): void {
        const container = this.config.target as HTMLElement;
        //always remove existing button before adding a new one (in case of re-rendering)
        document.getElementById(this.buttonId)?.remove();
        const imgBtn = document.createElement('img');
        const imgBtnSrc = this.imageButtonUrl + '?hl=' + this.getLocale();
        imgBtn.setAttribute('src', imgBtnSrc);
        imgBtn.setAttribute('id', this.buttonId);
        imgBtn.setAttribute('rel', 'opener');
        imgBtn.setAttribute('height', '46px');
        container.appendChild(imgBtn);
    }

    getWalletName(): string {
        return 'GOOGLE_PAY';
    }

    completeWalletOpaqueData(onSessionCreatedPayload: any): any {
        var serverSessionId = onSessionCreatedPayload['serverSessionId'];
        var publicDeviceId = onSessionCreatedPayload['publicDeviceId'];
        var publicWalletId = onSessionCreatedPayload['publicWalletId'];
        return {
            googlePay: {
                clientDeviceId: publicDeviceId,
                clientWalletAccountId: publicWalletId,
                googleServerSessionId: serverSessionId,
            },
        };
    }
}
